/**
 * Shelf reach — top cat head must enter the shelf zone.
 *
 * See docs/PLAN.md §4.6.
 */

import type { CatArchetype, CatState } from './types.js';

export interface ShelfZone {
  x: number;
  y: number;
  width: number;
  height: number;
  minReachHeight: number;
}

/** Cat with the smallest y (top of stack in y-down space). */
export function getTopCat(states: readonly CatState[]): CatState | null {
  if (states.length === 0) return null;
  let top: CatState = states[0] as CatState;
  for (const s of states) {
    if (s.y < top.y) top = s;
  }
  return top;
}

/** Head-top y of a cat body (smaller y = higher on screen). */
export function catHeadTopY(state: CatState, archetype: CatArchetype): number {
  return state.y - archetype.height / 2;
}

/** True when stack height clears the shelf's reach threshold. */
export function stackMeetsReachHeight(
  topCatY: number,
  bedSurfaceY: number,
  minReachHeight: number,
): boolean {
  const heightAboveBed = Math.max(0, bedSurfaceY - topCatY);
  return heightAboveBed >= minReachHeight;
}

/** Top cat's head overlaps the shelf zone. */
export function headOverlapsShelf(
  state: CatState,
  archetype: CatArchetype,
  shelf: ShelfZone,
): boolean {
  const headTop = catHeadTopY(state, archetype);
  const headBottom = state.y + archetype.height * 0.15;
  const headCenterX = state.x;
  const halfW = archetype.width * 0.35;
  const overlapsX = headCenterX + halfW >= shelf.x && headCenterX - halfW <= shelf.x + shelf.width;
  const overlapsY = headTop <= shelf.y + shelf.height && headBottom >= shelf.y;
  return overlapsX && overlapsY;
}

/** Paw can reach the shelf this frame. */
export function canReachShelf(
  states: readonly CatState[],
  archetypes: ReadonlyMap<string, CatArchetype>,
  shelf: ShelfZone,
  bedSurfaceY: number,
): boolean {
  const top = getTopCat(states);
  if (!top) return false;
  const archetype = archetypes.get(top.archetypeId);
  if (!archetype) return false;
  if (!stackMeetsReachHeight(top.y, bedSurfaceY, shelf.minReachHeight)) return false;
  return headOverlapsShelf(top, archetype, shelf);
}
